import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { getArticleBySlug } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || "");

  // Scroll to top on article change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  return (
    <>
      <Navbar />
      <main className="pt-20 pb-16 bg-background-50">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors duration-200">Início</Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
            <span className="text-foreground-600">Artigos</span>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
            <span className="text-foreground-600 line-clamp-1">{article.title.split(":")[0]}</span>
          </nav>

          {/* Article Header */}
          <header className="mb-10">
            <span className="inline-block text-xs text-primary-600 bg-primary-100 px-3 py-1 rounded-full mb-4">
              Guia Completo
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-500 text-sm">
              Atualizado em Junho 2026 · Tempo de leitura: 8 minutos
            </p>
          </header>

          {/* Featured Image */}
          <div className="rounded-2xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>

          {/* Article Body */}
          <div className="prose-custom text-foreground-700 leading-relaxed space-y-6 text-[15px]">
            {/* Introduction */}
            <section>
              <h2 className="font-heading text-2xl text-foreground-950 mt-10 mb-4">Introdução</h2>
              <p>{article.intro}</p>
            </section>

            {/* Unique Sections */}
            {article.sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="font-heading text-2xl text-foreground-950 mt-10 mb-4">{section.h2}</h2>
                <p>{section.content}</p>
              </section>
            ))}

            {/* Comparison Table (shared) */}
            <section>
              <h2 className="font-heading text-2xl text-foreground-950 mt-10 mb-4">Comparativo: Nossa Planilha vs Alternativas</h2>
              <div className="bg-background-100 rounded-2xl p-6 my-6">
                <h3 className="font-heading text-lg text-foreground-900 mb-4">CSSBuy Sheet vs Reddit vs Discord</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-300/60">
                        <th className="text-left py-3 pr-4 text-foreground-700 font-medium">Característica</th>
                        <th className="text-left py-3 pr-4 text-primary-600 font-medium">Nossa Planilha</th>
                        <th className="text-left py-3 pr-4 text-foreground-400 font-medium">Reddit</th>
                        <th className="text-left py-3 text-foreground-400 font-medium">Discord</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["Atualização", "Diária", "Irregular", "Variável"],
                        ["Links Verificados", "Sim (100%)", "Não", "Parcial"],
                        ["Conteúdo em PT-BR", "Completo", "Mínimo", "Mínimo"],
                        ["Organização", "Por categoria", "Aleatória", "Por canal"],
                        ["Guias e Tutoriais", "Inclusos", "Não", "Às vezes"],
                        ["Acesso", "Gratuito", "Gratuito", "Gratuito"],
                      ].map((row, i) => (
                        <tr key={i} className="border-b border-background-200/50">
                          <td className="py-2.5 pr-4 text-foreground-600">{row[0]}</td>
                          <td className="py-2.5 pr-4 text-primary-700 font-medium">{row[1]}</td>
                          <td className="py-2.5 pr-4 text-foreground-400">{row[2]}</td>
                          <td className="py-2.5 text-foreground-400">{row[3]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* FAQ (shared) */}
            <section>
              <h2 className="font-heading text-2xl text-foreground-950 mt-10 mb-4">Perguntas Frequentes</h2>
              <div className="space-y-4 my-6">
                {[
                  { q: "A CSSBuy Spreadsheet é gratuita?", a: "Sim! Nossa planilha CSSBuy é 100% gratuita. Não cobramos absolutamente nada pelo acesso aos links, categorias e guias. Todo o conteúdo é mantido pela comunidade e atualizado regularmente." },
                  { q: "Com que frequência os links são atualizados?", a: "Atualizamos nossa spreadsheet diariamente. Nossa equipe verifica manualmente cada link para garantir que todos estejam funcionando. Links quebrados ou produtos indisponíveis são removidos imediatamente." },
                  { q: "Preciso criar cadastro para acessar?", a: "Não! O acesso à nossa CSSBuy Spreadsheet é imediato e sem qualquer tipo de cadastro. Basta acessar o site, escolher uma categoria e começar a explorar os links." },
                  { q: "Posso sugerir novos links ou categorias?", a: "Com certeza! Estamos sempre abertos a sugestões da comunidade. Se você encontrou um bom link que não está na nossa planilha, entre em contato conosco." },
                ].map((faq, i) => (
                  <details key={i} className="bg-background-100 rounded-xl p-5 group cursor-pointer">
                    <summary className="font-medium text-sm text-foreground-900 list-none flex items-center justify-between">
                      {faq.q}
                      <i className="ri-add-line text-foreground-400 group-open:hidden w-4 h-4 flex items-center justify-center"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block w-4 h-4 flex items-center justify-center"></i>
                    </summary>
                    <p className="text-xs text-foreground-500 mt-3 leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
            </section>

            {/* Conclusion */}
            <section>
              <h2 className="font-heading text-2xl text-foreground-950 mt-10 mb-4">Conclusão</h2>
              <p>
                Depois de analisar detalhadamente o mercado de <strong>spreadsheets CSSBuy</strong>, fica claro por que a nossa solução se destaca. Somos a única fonte em português que oferece atualização diária, verificação de links, organização por categorias e guias completos — tudo 100% gratuito e sem necessidade de cadastro.
              </p>
              <p>
                Se você está cansado de perder tempo procurando links em fóruns, canais de Discord ou grupos de Telegram, a nossa <strong>planilha CSSBuy 2026</strong> é exatamente o que você precisa. Com mais de 5.000 links organizados em 14 categorias, você encontrará tudo o que procura em um só lugar.
              </p>
            </section>
          </div>

          {/* CTA */}
          <div className="mt-12 bg-primary-50 rounded-2xl p-8 text-center">
            <h2 className="font-heading text-xl text-foreground-950 mb-3">
              Pronto para começar suas compras?
            </h2>
            <p className="text-foreground-500 text-sm mb-6">
              Acesse agora a melhor CSSBuy Spreadsheet em português e encontre os melhores links para suas compras internacionais.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                VER SPREADSHEET
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-transparent text-foreground-700 border border-foreground-300/60 rounded-full font-medium text-sm hover:border-foreground-400 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Voltar ao Início
                <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
          </div>

          {/* Organization Schema */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Organization",
                "name": "CSSBuy Sheet",
                "url": "https://cssbuyspreadsheets.lat/",
                "logo": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png"
              }),
            }}
          />

          {/* Article Schema */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": article.title,
                "description": article.excerpt,
                "image": article.image,
                "author": { "@type": "Organization", "name": "CSSBuy Sheet", "url": "https://cssbuyspreadsheets.lat/" },
                "datePublished": "2026-06-27",
                "dateModified": "2026-06-27",
                "inLanguage": "pt-BR",
                "publisher": { "@type": "Organization", "name": "CSSBuy Sheet", "logo": { "@type": "ImageObject", "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png" } },
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": `https://cssbuyspreadsheets.lat/artigo/${slug || article.slug}`
                }
              }),
            }}
          />

          {/* FAQ Schema */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                  { "@type": "Question", "name": "A CSSBuy Spreadsheet é gratuita?", "acceptedAnswer": { "@type": "Answer", "text": "Sim! Nossa planilha CSSBuy é 100% gratuita." } },
                  { "@type": "Question", "name": "Com que frequência os links são atualizados?", "acceptedAnswer": { "@type": "Answer", "text": "Atualizamos nossa spreadsheet diariamente." } },
                  { "@type": "Question", "name": "Preciso criar cadastro para acessar?", "acceptedAnswer": { "@type": "Answer", "text": "Não! O acesso é imediato e sem qualquer tipo de cadastro." } },
                  { "@type": "Question", "name": "Posso sugerir novos links ou categorias?", "acceptedAnswer": { "@type": "Answer", "text": "Sim! Estamos sempre abertos a sugestões da comunidade." } },
                ],
              }),
            }}
          />

          {/* Breadcrumb Schema */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "CSSBuy Spreadsheet", "item": "https://cssbuyspreadsheets.lat/" },
                  { "@type": "ListItem", "position": 2, "name": article.title.split(":")[0] },
                ],
              }),
            }}
          />
        </article>
      </main>
      <Footer />
    </>
  );
}